import {
  defaultVisionEstimateResult,
  normalizeVisionEstimateResult,
  type VisionEstimateResult,
} from '../domain/visionEstimateSchema';
import { getJsonAuthHeaders } from './authHeaders';
import { buildApiUrl } from '../config/apiBaseUrl';
import { AI_ENDPOINTS, AI_TIMEOUTS_MS } from '../config/aiRuntimeConfig';
import { logOperationalError } from './operationalErrorLog';
import { buildVisionDiagnosticCode } from '../domain/visionFailure';

export type VisionEstimateClientMode = 'online' | 'offline' | 'error';

export type VisionEstimateClientRequest = {
  imageBase64: string;
  mimeType: 'image/jpeg' | 'image/png' | 'image/webp';
  projectContext?: {
    trade?: string;
    scope?: string;
    floorAreaM2?: number;
    /** Wymiary podane albo potwierdzone przez wykonawce. Kontekst skali, nie pomiar. */
    knownDimensions?: { length?: number; width?: number; height?: number };
    city?: string;
    region?: string;
  };
};

export type VisionEstimateClientResponse = {
  ok: boolean;
  mode: VisionEstimateClientMode;
  result: VisionEstimateResult;
  error?: string;
  reason?: string;
  provider?: string;
  /** Kod HTTP odpowiedzi. Brak = zadanie nie doszlo do serwera. */
  httpStatus?: number;
  /** Naglowek Content-Type odpowiedzi. Pozwala odroznic JSON od HTML fallbacku SPA. */
  contentType?: string;
};

const VISION_FALLBACK_MESSAGE =
  'Analiza zdjęcia jest chwilowo niedostępna. Możesz dalej policzyć wycenę ręcznie. Zdjęcie służy tylko jako kontekst, nie jako pomiar.';

export async function requestVisionEstimate(
  input: VisionEstimateClientRequest,
  timeoutMs: number = AI_TIMEOUTS_MS.vision
): Promise<VisionEstimateClientResponse> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const headers = await getJsonAuthHeaders();
    const response = await fetch(buildApiUrl(AI_ENDPOINTS.vision), {
      method: 'POST',
      headers,
      body: JSON.stringify(input),
      signal: controller.signal,
    });

    const contentType = response.headers.get('content-type') ?? undefined;
    const payload = await parseJsonSafely(response);

    // 2xx bez JSON-a to prawie zawsze fallback SPA zwracajacy index.html:
    // trasa /api/* nie dziala. Bez tego rozroznienia zepsuta trasa wygladala
    // identycznie jak "AI chwilowo offline".
    if (!payload) {
      const reason = response.ok ? 'non_json_response' : `vision_http_${String(response.status)}`;
      void logOperationalError(
        'api',
        buildVisionDiagnosticCode({ reason, httpStatus: response.status, contentType })
      );
      return fallbackVisionResponse(null, response.ok ? 'error' : 'error', reason, {
        httpStatus: response.status,
        contentType,
      });
    }

    if (!response.ok || payload.ok === false) {
      const reason =
        typeof payload.reason === 'string' && payload.reason.trim().length > 0
          ? payload.reason
          : response.ok
            ? 'vision_offline_response'
            : `vision_http_${String(response.status)}`;
      void logOperationalError(
        'api',
        buildVisionDiagnosticCode({ reason, httpStatus: response.status, contentType })
      );
      return fallbackVisionResponse(payload, response.ok ? 'offline' : 'error', reason, {
        httpStatus: response.status,
        contentType,
      });
    }

    return {
      ok: true,
      mode: payload.mode === 'online' ? 'online' : 'offline',
      result: normalizeVisionEstimateResult(payload.result),
      provider: typeof payload.provider === 'string' ? payload.provider : undefined,
      httpStatus: response.status,
      contentType,
    };
  } catch {
    // Zadanie nie doszlo do serwera: brak kodu HTTP i brak Content-Type.
    void logOperationalError('api', buildVisionDiagnosticCode({ reason: 'timeout_or_network_error' }));
    return fallbackVisionResponse(null, 'offline', 'timeout_or_network_error');
  } finally {
    window.clearTimeout(timeout);
  }
}

async function parseJsonSafely(response: Response): Promise<Record<string, unknown> | null> {
  try {
    const payload = await response.json();
    return payload && typeof payload === 'object' ? payload as Record<string, unknown> : null;
  } catch {
    return null;
  }
}

function fallbackVisionResponse(
  payload: Record<string, unknown> | null,
  mode: VisionEstimateClientMode,
  reason = 'vision_unavailable',
  transport?: { httpStatus?: number; contentType?: string }
): VisionEstimateClientResponse {
  return {
    ok: false,
    mode,
    ...(transport?.httpStatus === undefined ? {} : { httpStatus: transport.httpStatus }),
    ...(transport?.contentType === undefined ? {} : { contentType: transport.contentType }),
    result: normalizeVisionEstimateResult(payload?.result ?? defaultVisionEstimateResult),
    error:
      typeof payload?.error === 'string'
        ? payload.error
        : typeof payload?.userMessage === 'string'
          ? payload.userMessage
          : typeof payload?.message === 'string'
            ? payload.message
            : VISION_FALLBACK_MESSAGE,
    reason,
    provider: typeof payload?.provider === 'string' ? payload.provider : undefined,
  };
}
